import { Form, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Heading, Text } from 'components/typography';
import Button from 'components/button';
import FormError from 'components/form/error';
import LabeledInput from 'components/form/input';
import { useRef, useState } from 'react';
import TextArea from 'components/form/text-area';
import { Box, Card, Grid } from 'components/box';
import ContactNumberField from 'components/form/contact-number-field';
import SuccessModal from './modals/success';
import { FormValuesInterface, ValidFormValuesInterface } from './types-new';
import { YesNoRadio } from './shared';
import Select from 'components/form/select';
import noOp from '../../../util/noop';

/**
 * NOTE: Field names below are generated by Salesforce, and mapped here for ease of use
 */

const SALESFORCE_FIELDS = {
  oid: 'oid', // salesforce uses this ID to know that lead comes from ODO
  returnUrl: 'retURL',
  leadSource: 'lead_source',
  vatRegistered: '00Nfm0000012aOm',
  numberOfUniqueProducts: '00Nfm0000012aPA',
  productName: '00Nfm0000012aPB',
  availableStock: '00Nfm0000012aPF',
  costPrice: '00Nfm0000012aP7',
  retailPrice: '00Nfm0000012aPE',
  isSampleAvailable: '00Nfm0000012aOk',
  exactProductLink: '00Nfm0000012aPK',
  imageLink: '00Nfm0000012aP5',
  videoLink: '00Nfm0000012aPJ',
  otherProducts: '00Nfm000001xDvL',
  category: '00Nfm0000012aOh',
  retailOutlet: '00Nfm0000012aOj',
  supplyOtherOutlets: '00Nfm0000012aOl',
  productInfo: '00Nfm0000012aPH',
};

const CATEGORIES = [
  'Adult',
  'Apparel, Footwear and Fashion Accessories',
  'Baby',
  'Books',
  'Books and Media',
  'Camping, Sports and Outdoor',
  'Cleaning and Hygiene',
  'FMCG',
  'Gadgets, Gizmos and Gifts (importer/distributor of various categories)',
  'Furniture',
  'Health and Beauty',
  'Home and Kitchen',
  'Liquor (excluding wine)',
  'Luggage and Handbags',
  'Non-alcoholic Beverage and Consumables',
  'Petcare',
  'PPE',
  'Stationery',
  'Tech',
  'Tools and Garden',
  'Toys',
  'Travel and Experiences',
  'Vouchers',
  'Wine',
];

const ORGANISATION_ID = '00Dfm000001Bj1H';

const initialValues: FormValuesInterface = {
  [SALESFORCE_FIELDS.oid]: ORGANISATION_ID,
  [SALESFORCE_FIELDS.returnUrl]: 'https://www.onedayonly.co.za/',
  [SALESFORCE_FIELDS.leadSource]: 'lead_source',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  [SALESFORCE_FIELDS.vatRegistered]: '',
  [SALESFORCE_FIELDS.numberOfUniqueProducts]: '',
  [SALESFORCE_FIELDS.productName]: '',
  [SALESFORCE_FIELDS.availableStock]: '',
  [SALESFORCE_FIELDS.costPrice]: '',
  [SALESFORCE_FIELDS.retailPrice]: '',
  [SALESFORCE_FIELDS.isSampleAvailable]: '',
  [SALESFORCE_FIELDS.exactProductLink]: '',
  [SALESFORCE_FIELDS.imageLink]: '',
  [SALESFORCE_FIELDS.videoLink]: '',
  [SALESFORCE_FIELDS.otherProducts]: '',
  [SALESFORCE_FIELDS.category]: '',
  [SALESFORCE_FIELDS.retailOutlet]: '',
  [SALESFORCE_FIELDS.supplyOtherOutlets]: '',
  [SALESFORCE_FIELDS.productInfo]: '',
};

const FORM_ACTION =
  'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=00Dfm000001Bj1H';

const SupplierFormNew = () => {
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [buttonLoadState, setButtonLoadState] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = () => {
    setButtonLoadState(true);

    setTimeout(() => {
      if (formRef.current) {
        formRef.current.submit();
      }
      setSuccessModalOpen(true);
      setButtonLoadState(false);
    }, 2500);
  };

  return (
    <>
      <Heading textAlign="center" fontSize={5} mb={3} color="primary">
        Want to be a supplier?
      </Heading>

      <Text textAlign="center" lineHeight={1.6} mb={4}>
        If you&apos;ve got the (literal) goods, please fill in the form below!
        <br />
        Our sales team will review your application and get back to you, snappy.
      </Text>

      <Form<FormValuesInterface>
        initialValues={initialValues}
        onSubmit={() => noOp}
        mutators={{ ...arrayMutators }}
      >
        {({ submitting, values, hasValidationErrors }) => (
          <form ref={formRef} action={FORM_ACTION} method="POST">
            <Grid>
              {/* Contact Details */}
              <Card p={[3, 4]} borderRadius={3}>
                <Grid>
                  <Heading fontSize={3} mb={2} color="primary">
                    Contact Details
                  </Heading>

                  <input
                    type="hidden"
                    name={SALESFORCE_FIELDS.oid}
                    value={ORGANISATION_ID}
                  />
                  <input
                    type="hidden"
                    name={SALESFORCE_FIELDS.returnUrl}
                    value="https://www.onedayonly.co.za/"
                  />
                  <input
                    type="hidden"
                    name={SALESFORCE_FIELDS.leadSource}
                    value="Supplier Online Form"
                  />

                  <Field<ValidFormValuesInterface['first_name']>
                    name="first_name"
                    validate={value =>
                      !value ? 'Please enter your name' : null
                    }
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="First name *"
                        placeholder="Enter your name"
                        maxLength={40}
                        required
                      />
                    )}
                  </Field>

                  <Field<ValidFormValuesInterface['last_name']>
                    name="last_name"
                    validate={value =>
                      !value ? 'Please enter your last name' : null
                    }
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Last name *"
                        placeholder="Enter your last name"
                        maxLength={80}
                        required
                      />
                    )}
                  </Field>

                  <Field<ValidFormValuesInterface['email']>
                    name="email"
                    validate={value =>
                      !value ? 'Please enter your email address' : null
                    }
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        label="Email address *"
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        placeholder="Enter your email address"
                        type="email"
                        maxLength={80}
                        required
                      />
                    )}
                  </Field>

                  <ContactNumberField
                    inputLabel="Phone number *"
                    fieldName="phone"
                    isRequired
                  />
                </Grid>
              </Card>

              {/* Company Details */}
              <Card p={[3, 4]} borderRadius={3}>
                <Grid>
                  <Heading fontSize={3} mb={2} color="primary">
                    About your business
                  </Heading>

                  <Field<ValidFormValuesInterface['company']>
                    name="company"
                    validate={value =>
                      !value ? 'Please enter your company name' : null
                    }
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Company name *"
                        placeholder="Enter your company name"
                        maxLength={40}
                        required
                      />
                    )}
                  </Field>

                  <Field<ValidFormValuesInterface['url']> name="url">
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Company website or social media link (if applicable)"
                        placeholder="Enter your website or social media url"
                        maxLength={80}
                      />
                    )}
                  </Field>

                  <YesNoRadio name={SALESFORCE_FIELDS.vatRegistered}>
                    <Text mb={2} fontWeight="bold">
                      Are you VAT registered? *
                    </Text>
                    <Text mb={3}>
                      Please note that we can still do business with you if you
                      are not VAT registered
                    </Text>
                  </YesNoRadio>

                  <Field<ValidFormValuesInterface['00Nfm0000012aOh']>
                    name={SALESFORCE_FIELDS.category}
                    validate={value =>
                      !value ? 'Please select a category' : null
                    }
                  >
                    {({ input, meta }) => (
                      <Box>
                        <Select
                          {...input}
                          invalid={meta.error && meta.touched}
                          label="Category *"
                        >
                          <option value="">Select a category...</option>
                          {CATEGORIES.map(c => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </Select>
                        {meta.error && meta.touched && (
                          <FormError>{meta.error}</FormError>
                        )}
                      </Box>
                    )}
                  </Field>

                  <Field<
                    ValidFormValuesInterface['description']
                  > name="description">
                    {({ input, meta }) => (
                      <TextArea
                        label="Tell us a bit about your company and what you offer"
                        {...input}
                        type="text"
                        placeholder="Provide us with a summary of your business"
                        invalid={meta.error && meta.touched}
                        error={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Grid>
              </Card>

              <Card p={[3, 4]} borderRadius={3}>
                <Grid>
                  <Heading fontSize={3} mb={2} color="primary">
                    About your products
                  </Heading>

                  <Field<ValidFormValuesInterface['00Nfm0000012aPH']>
                    name={SALESFORCE_FIELDS.productInfo}
                  >
                    {({ input, meta }) => (
                      <TextArea
                        label="Tell us about your products"
                        {...input}
                        type="text"
                        placeholder="Provide us with a summary of your product"
                        error={meta.touched && meta.error}
                      />
                    )}
                  </Field>

                  <Field<FormValuesInterface['00Nfm0000012aPA']>
                    name={SALESFORCE_FIELDS.numberOfUniqueProducts}
                    validate={value =>
                      !value
                        ? 'Please enter the number of unique products'
                        : null
                    }
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Number of unique products *"
                        placeholder="e.g. 5"
                        min={0}
                        type="number"
                      />
                    )}
                  </Field>

                  <YesNoRadio name={SALESFORCE_FIELDS.retailOutlet}>
                    <Text fontWeight="bold">
                      Do you have a retail outlet? *
                    </Text>
                  </YesNoRadio>

                  <YesNoRadio name={SALESFORCE_FIELDS.supplyOtherOutlets}>
                    <Text fontWeight="bold">
                      Do you supply other retail outlets and online stores? *
                    </Text>
                  </YesNoRadio>

                  <Field<ValidFormValuesInterface['00Nfm0000012aPB']>
                    name={SALESFORCE_FIELDS.productName}
                    validate={value =>
                      !value ? 'Please enter your product name' : null
                    }
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Product name *"
                        placeholder="Enter your product name"
                        maxLength={255}
                      />
                    )}
                  </Field>

                  <Field<ValidFormValuesInterface['00Nfm0000012aPF']>
                    name={SALESFORCE_FIELDS.availableStock}
                    validate={value =>
                      !value
                        ? 'Please enter the amount of available stock'
                        : null
                    }
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Stock currently available *"
                        placeholder="e.g. 10"
                        min={0}
                        type="number"
                      />
                    )}
                  </Field>

                  {values[SALESFORCE_FIELDS.vatRegistered] === 'Yes' && (
                    <Field<ValidFormValuesInterface['00Nfm0000012aP7']>
                      name={SALESFORCE_FIELDS.costPrice}
                      validate={value =>
                        !value
                          ? 'Please enter the lowest possible cost price'
                          : null
                      }
                    >
                      {({ input, meta }) => (
                        <LabeledInput
                          {...input}
                          invalid={meta.error && meta.touched}
                          error={meta.error}
                          label="Lowest possible cost price (excl. VAT)*"
                          placeholder="e.g. 20"
                          min={0}
                          type="number"
                        />
                      )}
                    </Field>
                  )}

                  {values[SALESFORCE_FIELDS.vatRegistered] === 'No' && (
                    <Field<ValidFormValuesInterface['00Nfm0000012aP7']>
                      name={SALESFORCE_FIELDS.costPrice}
                      validate={value =>
                        !value
                          ? 'Please enter the lowest possible cost price'
                          : null
                      }
                    >
                      {({ input, meta }) => (
                        <LabeledInput
                          {...input}
                          invalid={meta.error && meta.touched}
                          error={meta.error}
                          label="Lowest possible cost price (incl. VAT)*"
                          placeholder="e.g. 20"
                          min={0}
                          type="number"
                        />
                      )}
                    </Field>
                  )}

                  <Field<ValidFormValuesInterface['00Nfm0000012aPE']>
                    name={SALESFORCE_FIELDS.retailPrice}
                    validate={value =>
                      !value
                        ? 'Please enter the retail price of the product in market/store'
                        : null
                    }
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Retail price in the market/store *"
                        placeholder="e.g. 300"
                        min={0}
                        type="number"
                      />
                    )}
                  </Field>

                  <YesNoRadio name={SALESFORCE_FIELDS.isSampleAvailable}>
                    <Text fontWeight="bold">
                      Do you have a sample available? *
                    </Text>
                  </YesNoRadio>

                  <Field<ValidFormValuesInterface['00Nfm0000012aPK']>
                    name={SALESFORCE_FIELDS.exactProductLink}
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Website link to this exact product"
                        placeholder="Please enter the website link"
                        maxLength={255}
                      />
                    )}
                  </Field>

                  <Field<ValidFormValuesInterface['00Nfm0000012aP5']>
                    name={SALESFORCE_FIELDS.imageLink}
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Image Link"
                        placeholder="Please enter the image link"
                        maxLength={255}
                      />
                    )}
                  </Field>

                  <Field<ValidFormValuesInterface['00Nfm0000012aPJ']>
                    name={SALESFORCE_FIELDS.videoLink}
                  >
                    {({ input, meta }) => (
                      <LabeledInput
                        {...input}
                        invalid={meta.error && meta.touched}
                        error={meta.error}
                        label="Video Link"
                        placeholder="Please enter the video link"
                        maxLength={255}
                      />
                    )}
                  </Field>
                </Grid>
              </Card>

              <Card p={[3, 4]} borderRadius={3}>
                <Field<ValidFormValuesInterface['00Nfm000001xDvL']>
                  name={SALESFORCE_FIELDS.otherProducts}
                >
                  {({ input, meta }) => (
                    <TextArea
                      label="Do you have any other products you'd like to sell?"
                      {...input}
                      type="text"
                      placeholder="Use this field to list any additional products you'd like to offer.
                      You can type out the product names and details, add links to products,
                      or include a link to a Google sheet if you have a longer list."
                      error={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Card>

              <Button
                mx="auto"
                mt={3}
                onClick={handleSubmit}
                width={['100%', 'auto']}
                loading={buttonLoadState}
                disabled={submitting || hasValidationErrors}
                size="large"
                stretch
              >
                {submitting ? 'Submitting' : 'Submit'}
              </Button>
            </Grid>
          </form>
        )}
      </Form>

      <SuccessModal
        isOpen={successModalOpen}
        close={() => setSuccessModalOpen(false)}
      />
    </>
  );
};

export default SupplierFormNew;
